import { useState } from 'react';
import { cn } from '@/lib/utils';
import type { Plano } from '@/types/subscription.types';
import type { OrcamentoDraftData } from '../draftStore';
import { brl } from '../calc';
import { Field, StepHeader } from '../ui';
import { inputCls, linkBtnCls, nf } from '../styles';

type Software = OrcamentoDraftData['software'];
type PlanoId = OrcamentoDraftData['planoId'];
type Modo = 'nenhum' | 'plano' | 'personalizado';

const SEM_PLANO = 'Plano personalizado';

function modoInicial(planoId: PlanoId, software: Software): Modo {
  if (planoId !== 'custom') return 'plano';
  return software.mensal > 0 ? 'personalizado' : 'nenhum';
}

/**
 * Plano como lista de escolha única. O valor mensal só vira campo no
 * "personalizado"; o período (quase sempre 12 meses) fica em texto até pedirem.
 */
export function SoftwareStep({
  planos,
  planoId,
  software,
  onChange,
}: {
  planos: Plano[];
  planoId: PlanoId;
  software: Software;
  onChange: (planoId: PlanoId, software: Software) => void;
}) {
  const [modo, setModo] = useState<Modo>(() => modoInicial(planoId, software));
  const [editMeses, setEditMeses] = useState(software.meses !== 12);

  const escolherNenhum = () => {
    setModo('nenhum');
    onChange('custom', { ...software, plano: SEM_PLANO, mensal: 0 });
  };
  const escolherPlano = (p: Plano) => {
    setModo('plano');
    onChange(p.id, { ...software, plano: p.nome, mensal: (p.preco_mensal_centavos ?? 0) / 100 });
  };
  const escolherPersonalizado = () => {
    setModo('personalizado');
    onChange('custom', { ...software, plano: SEM_PLANO });
  };

  const total = software.mensal * software.meses;

  return (
    <div>
      <StepHeader
        title="Assinatura do software"
        description="Opcional. Aparece na proposta separada do valor do kit, como mensalidade."
      />

      <div className="flex flex-col gap-2" role="radiogroup" aria-label="Plano de software">
        <Opcao
          checked={modo === 'nenhum'}
          onSelect={escolherNenhum}
          title="Sem software"
          detail="Só o kit"
        />
        {planos.map((p) => (
          <Opcao
            key={p.id}
            checked={modo === 'plano' && planoId === p.id}
            onSelect={() => escolherPlano(p)}
            title={p.nome}
            detail={`${brl((p.preco_mensal_centavos ?? 0) / 100)} por mês`}
          />
        ))}
        <Opcao
          checked={modo === 'personalizado'}
          onSelect={escolherPersonalizado}
          title="Valor combinado com o cliente"
          detail="Você define a mensalidade"
        />
      </div>

      {modo === 'personalizado' && (
        <div className="mt-4 max-w-xs">
          <Field label="Mensalidade (R$)">
            <input
              type="number"
              min={0}
              className={inputCls}
              value={software.mensal}
              onChange={(e) => onChange('custom', { ...software, mensal: nf(e.target.value) })}
              autoFocus
            />
          </Field>
        </div>
      )}

      {modo !== 'nenhum' && (
        <div className="mt-4 text-sm text-text-secondary">
          {editMeses ? (
            <div className="max-w-xs">
              <Field label="Período do contrato (meses)">
                <input
                  type="number"
                  min={1}
                  className={inputCls}
                  value={software.meses}
                  onChange={(e) => onChange(planoId, { ...software, meses: nf(e.target.value) })}
                />
              </Field>
            </div>
          ) : (
            <p>
              Contrato de <span className="text-text-title">{software.meses} meses</span>
              {total > 0 && (
                <>
                  , total de <span className="text-text-title">{brl(total)}</span>
                </>
              )}
              .{' '}
              <button type="button" className={linkBtnCls} onClick={() => setEditMeses(true)}>
                mudar período
              </button>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Opcao({
  checked,
  onSelect,
  title,
  detail,
}: {
  checked: boolean;
  onSelect: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={checked}
      onClick={onSelect}
      className={cn(
        'flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
        checked
          ? 'border-primary bg-primary/10'
          : 'border-border bg-muted hover:border-border-strong'
      )}
    >
      <span
        className={cn(
          'flex size-4 shrink-0 items-center justify-center rounded-full border',
          checked ? 'border-primary' : 'border-border-strong'
        )}
      >
        {checked && <span className="size-2 animate-pop rounded-full bg-primary" />}
      </span>
      <span className="flex-1 text-sm font-medium text-text-title">{title}</span>
      <span className="text-sm text-text-secondary">{detail}</span>
    </button>
  );
}
